import { useState } from 'react'
import { isEmailValid } from 'src/common/helpers'
import { useNavigate } from 'react-router-dom'
import type { SignInOutput } from 'aws-amplify/auth'
import { useAuth } from 'src/common/context'
import { recordSigninEvent } from 'src/common/api/accountSecurity'
import { passkeyErrorMessage } from 'src/common/context/auth/passkeyErrors'

export type LoginStep = 'email' | 'passkey' | 'password'

interface State {
  step: LoginStep
  email: string
  error: string
  loading: boolean
  // Passkey step: true once a ceremony has been attempted (so the button/copy reflects a retry).
  passkeyAttempted: boolean
  // Passkey step: whether the "try another way" options are shown.
  tryAnotherOpen: boolean
  // First-time TOTP enrollment (reuses the SetupMfa modal).
  setupMfa: boolean
  setupMfaParams: { email: string; secret: string }
}

const initialState: State = {
  step: 'email',
  email: '',
  error: '',
  loading: false,
  passkeyAttempted: false,
  tryAnotherOpen: false,
  setupMfa: false,
  setupMfaParams: { email: '', secret: '' }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const nextStepOf = (r: SignInOutput) => r.nextStep as any

const useLogin = () => {
  const navigate = useNavigate()
  const { login, confirmSignInStep, isPasskeySupported } = useAuth()
  const [state, setState] = useState<State>(initialState)

  const patch = (p: Partial<State>) => setState((s) => ({ ...s, ...p }))

  const toReset = (email: string) =>
    navigate({ pathname: '/reset-password', search: '?newPasswordRequired=true' }, { replace: true, state: { email } })

  // Branch after the password (or a completing) step.
  const handleAfterPassword = (result: SignInOutput, email: string) => {
    const step = nextStepOf(result).signInStep
    if (step === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
      navigate({ pathname: '/mfa' }, { replace: true, state: { email } })
    } else if (step === 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP') {
      patch({
        setupMfa: true,
        setupMfaParams: { email, secret: nextStepOf(result).totpSetupDetails?.sharedSecret },
        loading: false
      })
    } else if (step === 'RESET_PASSWORD' || step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      toReset(email)
    } else if (result.isSignedIn) {
      recordSigninEvent('password').catch(() => undefined)
      navigate('/')
    } else {
      patch({ loading: false })
    }
  }

  // (Re)start the USER_AUTH flow for the current email. Returns the first-factor-selection result.
  const startSignIn = (email: string) => login({ username: email, options: { authFlowType: 'USER_AUTH' } })

  // Run the passkey ceremony. `restart` re-initiates sign-in first (used for retries / after a failed attempt,
  // when the Cognito session is no longer at the factor-selection step).
  const runPasskey = async (restart: boolean) => {
    patch({ loading: true, error: '', passkeyAttempted: true, tryAnotherOpen: false })
    try {
      if (restart) await startSignIn(state.email)
      const result = await confirmSignInStep('WEB_AUTHN')
      if (result.isSignedIn) {
        recordSigninEvent('passkey').catch(() => undefined)
        navigate('/')
        return
      }
      patch({ loading: false, error: 'Passkey sign-in could not be completed. Try another way.', tryAnotherOpen: true })
    } catch (e) {
      patch({ loading: false, error: passkeyErrorMessage(e, 'signin'), tryAnotherOpen: true })
    }
  }

  // Switch to the password factor (restarts sign-in so it works whether or not a passkey was just attempted).
  const usePassword = async () => {
    patch({ loading: true, error: '', tryAnotherOpen: false })
    try {
      await startSignIn(state.email)
      const result = await confirmSignInStep('PASSWORD')
      if (nextStepOf(result).signInStep === 'CONFIRM_SIGN_IN_WITH_PASSWORD') {
        patch({ step: 'password', loading: false })
      } else {
        handleAfterPassword(result, state.email)
      }
    } catch (e) {
      patch({ loading: false, error: (e as Error).message })
    }
  }

  const submitEmail = async (rawEmail: string) => {
    const email = rawEmail.trim().toLowerCase()
    if (!email) return patch({ error: 'Email is required' })
    if (!isEmailValid(email)) return patch({ error: 'Invalid email address' })
    patch({ loading: true, error: '', email })
    try {
      const result = await startSignIn(email)
      const step = nextStepOf(result).signInStep
      if (step === 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION') {
        const available = nextStepOf(result).availableChallenges as string[] | undefined
        const canPasskey = !!available?.includes('WEB_AUTHN') && isPasskeySupported()
        if (canPasskey) {
          patch({ step: 'passkey', loading: false, passkeyAttempted: false, tryAnotherOpen: false })
          // Auto-invoke while the email-submit gesture is still live; otherwise the passkey step shows a button.
          const userActivation = (navigator as Navigator & { userActivation?: { isActive: boolean } }).userActivation
          if (userActivation?.isActive) {
            await runPasskey(false)
          }
        } else {
          // No passkey for this user (or unsupported browser) → go straight to the password factor.
          const next = await confirmSignInStep('PASSWORD')
          if (nextStepOf(next).signInStep === 'CONFIRM_SIGN_IN_WITH_PASSWORD') {
            patch({ step: 'password', loading: false })
          } else {
            handleAfterPassword(next, email)
          }
        }
      } else if (step === 'CONFIRM_SIGN_IN_WITH_PASSWORD') {
        patch({ step: 'password', loading: false })
      } else if (step === 'RESET_PASSWORD' || step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        toReset(email)
      } else if (result.isSignedIn) {
        navigate('/')
      } else {
        patch({ loading: false })
      }
    } catch (e) {
      patch({ loading: false, error: (e as Error).message })
    }
  }

  const submitPassword = async (password: string) => {
    if (!password) return patch({ error: 'Password is required' })
    patch({ loading: true, error: '' })
    try {
      const result = await confirmSignInStep(password)
      handleAfterPassword(result, state.email)
    } catch (e) {
      patch({ loading: false, error: (e as Error).message })
    }
  }

  const backToEmail = () => setState(initialState)

  return {
    state,
    submitEmail,
    retryPasskey: () => runPasskey(true),
    usePasskeyButton: () => runPasskey(!state.passkeyAttempted ? false : true),
    openTryAnother: () => patch({ tryAnotherOpen: true }),
    usePassword,
    submitPassword,
    backToEmail
  }
}

export default useLogin
